// Small per-clock SLA pill shared by the Tickets list (compact 3-row cell)
// and the ticket-show SLA panel. Maps SlaState → color/label and emits a
// short "12m left" / "OVERDUE 4m" / "Met 18m ago" / "—" caption next to it.
//
// Source of truth for state strings: AgentConvo#sla_*_state on the Ruby side
// (see app/use_cases/portal/ticket_presenter.rb#sla_payload).

import { cn } from "@/lib/cn"
import type { SlaState } from "@/types/ticket"

const STATE_DOT: Record<SlaState, string> = {
  not_tracked: "bg-app-subtle/40",
  not_yet:     "bg-status-success",
  near_breach: "bg-status-warning",
  breached:    "bg-status-error",
  met:         "bg-app-subtle/70",
}

const STATE_TEXT: Record<SlaState, string> = {
  not_tracked: "text-app-subtle",
  not_yet:     "text-status-success",
  near_breach: "text-status-warning",
  breached:    "text-status-error",
  met:         "text-app-muted",
}

type Props = {
  /** Short label printed in the leading column ("FR" / "Idle" / "Res"). */
  label: string
  state: SlaState
  /** ISO timestamp of the active due_at (null when met / not tracked). */
  dueAt?: string | null
  /** ISO timestamp the clock was satisfied (first response only). */
  metAt?: string | null
  /** ISO timestamp the breach was first recorded. */
  breachedAt?: string | null
  /** Idle-only: number of times this clock has rolled past its due_at. */
  breachCount?: number
  /** Optional CSS for the row container. */
  className?: string
}

export function SlaPill({
  label,
  state,
  dueAt,
  metAt,
  breachedAt,
  breachCount,
  className,
}: Props) {
  const caption = describe({ state, dueAt, metAt, breachedAt })
  const overflow =
    breachCount !== undefined && breachCount > 0 ? ` (×${breachCount})` : ""
  return (
    <div className={cn("flex items-center gap-1.5 leading-tight", className)}>
      <span className="w-8 shrink-0 text-[10px] font-medium uppercase tracking-wide text-app-subtle">
        {label}
      </span>
      <span
        className={cn("h-1.5 w-1.5 shrink-0 rounded-full", STATE_DOT[state])}
        aria-hidden
      />
      <span className={cn("text-xs tabular-nums", STATE_TEXT[state])}>
        {caption}
        {overflow && <span className="ml-1 opacity-80">{overflow.trim()}</span>}
      </span>
    </div>
  )
}

// Plain-text caption — caller wraps in the colored span.
function describe({
  state,
  dueAt,
  metAt,
  breachedAt,
}: {
  state: SlaState
  dueAt?: string | null
  metAt?: string | null
  breachedAt?: string | null
}): string {
  if (state === "not_tracked") return "—"
  if (state === "met") {
    if (metAt) return `Met ${fmtDelta(metAt, "ago")}`
    return "Met"
  }
  if (state === "breached") {
    const anchor = breachedAt ?? dueAt
    if (anchor) return `OVERDUE ${fmtDelta(anchor, "since")}`
    return "OVERDUE"
  }
  // not_yet / near_breach
  if (dueAt) return `${fmtRemaining(dueAt)} left`
  return "—"
}

// "in X" representation rendered as just "X".
function fmtRemaining(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now()
  if (Number.isNaN(ms)) return "—"
  if (ms <= 0) return "0m"
  return shortDuration(Math.floor(ms / 1000))
}

// "X ago" / "X since" → returns just the duration; caller composes the verb.
function fmtDelta(iso: string, _form: "ago" | "since"): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms)) return "—"
  if (ms <= 0) return "0m"
  return shortDuration(Math.floor(ms / 1000))
}

// "12m" / "1h 6m" / "3d 4h". Mirrors the old presenter format_age helper so
// the visual rhythm of the column doesn't change.
function shortDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  const mr = m % 60
  if (h < 24) return mr ? `${h}h ${mr}m` : `${h}h`
  const d = Math.floor(h / 24)
  const hr = h % 24
  return hr ? `${d}d ${hr}h` : `${d}d`
}
